import {
  Box,
  Button,
  Container,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import DmsLocations from "../../compenents/Integration/DmsSetup/DmsLocations";
import DmsProducts from "../../compenents/Integration/DmsSetup/DmsProducts";
import InitializeShopify from "../../compenents/Integration/DmsSetup/InitializeShopify";
import {
  addProductTypeList,
  useGetDealerLocationsMutation,
} from "../../redux/reducer/features/Integration/DmsSetupSlice";
import { AppState } from "../../redux/reducer/rootReducer";
import DmsManufacturers from "../../compenents/Integration/DmsSetup/DmsManufacturers";
import { useGetManufacturersQuery } from "../../redux/reducer/features/Product/productSlice";
import { useLocation } from "react-router-dom";
import PlatformSelector from "../../compenents/Integration/DmsSetup/PlatformSelector";
import PlatformSetting from "../../compenents/Integration/DmsSetup/PlatformSetting";

interface DmsSetupProps {}

const DmsSetup: React.FunctionComponent<DmsSetupProps> = (props) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set<number>());
  const [dmsType, setDmsType] = React.useState<number | undefined>(undefined);
  const everestDmsDealerId = useSelector(
    (state: AppState) => state.authReducer.everestDmsDealerId,
  );
  const catalogManufacturers = useGetManufacturersQuery();
  const [dmsLocationMutation, dmsLocationMutationResult] =
    useGetDealerLocationsMutation();
  useEffect(() => {
    dmsLocationMutation({
      everestDmsDealerId: everestDmsDealerId ?? "",
    });
  }, []);

  useEffect(() => {
    if (dmsLocationMutationResult.isSuccess) {
      const locations = dmsLocationMutationResult.data?.entityList;
      if (locations && locations.length > 0) {
        setDmsType(locations[0].dmsType);
      }
    }
  }, [dmsLocationMutationResult]);

  const isStepSkipped = (step: number) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  React.useEffect(() => {
    dispatch(addProductTypeList(["Wholegoods"]));
  }, []);

  let steps = [
    {
      label: "Select Platform",
      component: <PlatformSelector handleNext={handleNext} />,
      isRequired: true,
    },
    {
      label: "Platform Setting",
      component: (
        <PlatformSetting handleBack={handleBack} handleNext={handleNext} />
      ),
      isRequired: true,
    },
    {
      label: "Locations",
      component: (
        <DmsLocations
          locations={dmsLocationMutationResult?.data?.entityList ?? []}
          handleBack={handleBack}
          handleNext={handleNext}
        />
      ),
      isRequired: true,
    },
    {
      label: "Manufacturers",
      component: (
        <DmsManufacturers
          handleBack={handleBack}
          handleNext={handleNext}
          catalogManufacturers={
            catalogManufacturers.data && catalogManufacturers.data.entityList
              ? catalogManufacturers.data.entityList
              : []
          }
          dmsType={dmsType}
        />
      ),
      isRequired: true,
    },
    {
      label: "Fetch DMS Data",
      component: (
        <DmsProducts handleBack={handleBack} handleNext={handleNext} />
      ),
      isRequired: true,
    },
    {
      label: "Setup Creation",
      component: (
        <InitializeShopify
          handleBack={handleBack}
          locations={dmsLocationMutationResult.data?.entityList ?? []}
        />
      ),
      isRequired: true,
    },
  ];

  if (location.pathname === "/addSite") {
    steps = steps.filter((step) => step.label !== "Manufacturers");
  }

  return (
    <Container maxWidth={false}>
      <Box sx={{ width: "100%", backgroundColor: "white" }} pt={2}>
        <Stepper activeStep={activeStep}>
          {steps.map((item, index) => {
            const stepProps: { completed?: boolean } = {};
            const labelProps: {
              optional?: React.ReactNode;
            } = {};

            if (isStepSkipped(index)) {
              stepProps.completed = false;
            }
            return (
              <Step key={item.label} {...stepProps} disabled>
                <StepLabel {...labelProps}>{item.label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
        {activeStep === steps.length ? (
          <React.Fragment>
            <Typography sx={{ mt: 2, mb: 1 }}>
              All steps completed - you&apos;re finished
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
              <Box sx={{ flex: "1 1 auto" }} />
              <Button onClick={handleReset}>Reset</Button>
            </Box>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Typography sx={{ mt: 1, mb: 0 }}>
              {steps[activeStep].component}
            </Typography>
          </React.Fragment>
        )}
      </Box>
    </Container>
  );
};

export default DmsSetup;
